import {
    all,
    call,
    cancel,
    delay,
    fork,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import { stopSubmit } from 'redux-form';

// Constants
import {
    TIMESHEET_TIMESHEET_ENTRIES_MANAGE_REQUESTED,
    TIMESHEET_TIMESHEET_ENTRIES_MANAGE_CANCELED,
    TIMESHEET_TIMESHEET_ENTRIES_APPROVE_REQUESTED
} from './constants';

// Actions
import { enqueueSnackbar } from '../notifications/actions';
import { updateQueryString } from '../router/actions';
import {
    setTimesheetTimesheetEntriesAPIErrors,
    resetTimesheetTimesheetEntriesAPIErrors
} from '../timesheetTimesheetEntriesAPIErrors/actions';
import {
    succeededManageTimesheetTimesheetEntries,
    failedManageTimesheetTimesheetEntries,
    succeededApproveTimesheetTimesheetEntries,
    failedApproveTimesheetTimesheetEntries,
    mergeTimesheetTimesheetEntries,
    removeTimesheetTimesheetEntries
} from './actions';

// Selectors
import { idTokenSelector } from '../auth/selectors';
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* manageTimesheetTimesheetEntries(action) {
    yield delay(1000);

    const {
        payload: { data }
    } = action;

    const idToken = yield select(idTokenSelector);

    try {
        const response = yield call(axios, {
            method: 'put',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries/bulk`,
            headers: {
                Authorization: idToken
            },
            data
        });

        yield put(resetTimesheetTimesheetEntriesAPIErrors());

        // Succeeded Manage Timesheet Timesheet Entries
        yield put(succeededManageTimesheetTimesheetEntries(response));

        const deletedTimesheetEntries = response.data.delete;
        const createdTimesheetEntries = response.data.create;
        const updatedTimesheetEntries = response.data.update;

        if (deletedTimesheetEntries) {
            const deletedTimesheetEntryUuids = deletedTimesheetEntries.map(
                value => value.uuid
            );

            // Remove Timesheet Timesheet Entries
            yield put(
                removeTimesheetTimesheetEntries(deletedTimesheetEntryUuids)
            );

            // Update Query String
            yield put(
                updateQueryString({
                    deleteTimesheetEntriesModal: undefined
                })
            );
        }

        if (createdTimesheetEntries) {
            const createdTimesheetEntriesKeyedByUuid = keyBy(
                createdTimesheetEntries,
                'uuid'
            );

            // Merge Timesheet Timesheet Entries
            yield put(
                mergeTimesheetTimesheetEntries(
                    createdTimesheetEntriesKeyedByUuid
                )
            );

            // Update Query String
            yield put(
                updateQueryString({
                    timesheetEntryModal: undefined,
                    selectedTimesheetEntry: undefined,
                    defaultValues: undefined
                })
            );
        }

        if (updatedTimesheetEntries) {
            const updatedTimesheetEntriesKeyedByUuid = keyBy(
                updatedTimesheetEntries,
                'uuid'
            );

            // Merge Timesheet Timesheet Entries
            yield put(
                mergeTimesheetTimesheetEntries(
                    updatedTimesheetEntriesKeyedByUuid
                )
            );
        }
    } catch (error) {
        // Failed Update Timesheet Timesheet Entries
        yield put(failedManageTimesheetTimesheetEntries(error));

        const errors = get(error, 'response.data.errors');

        if (errors) {
            yield put(setTimesheetTimesheetEntriesAPIErrors(errors));

            // Update Query String
            yield put(
                updateQueryString({
                    timesheetEntriesErrorModal: true
                })
            );
        } else {
            const message = get(
                error,
                'response.data.message',
                'There was an error. Please try again later.'
            );

            // Enqueue Snackbar
            yield put(
                enqueueSnackbar({
                    message,
                    options: { variant: 'error' }
                })
            );
        }
    }
}

export function* watchTimesheetTimesheetEntriesManageRequested() {
    let lastTask;
    while (true) {
        const [requested] = yield race([
            take(TIMESHEET_TIMESHEET_ENTRIES_MANAGE_REQUESTED),
            take(TIMESHEET_TIMESHEET_ENTRIES_MANAGE_CANCELED)
        ]);
        if (lastTask) {
            yield cancel(lastTask);
        }
        if (requested) {
            lastTask = yield fork(manageTimesheetTimesheetEntries, requested);
        }
    }
}

export function* approveTimesheetTimesheetEntries(action) {
    const {
        payload: { timesheetEntryUuids }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    // Select Current Organization Id
    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/TimesheetReviews`;

    try {
        const response = yield call(axios, {
            method: 'post',
            url,
            headers: {
                Authorization: idToken
            },
            data: {
                organizationId,
                isApproved: true,
                timesheetEntryUuids
            }
        });

        const { timesheetEntries } = response.data;
        const timesheetEntriesKeyedByUuid = keyBy(timesheetEntries, 'uuid');

        // Merge Timesheet Timesheet Entries
        yield put(mergeTimesheetTimesheetEntries(timesheetEntriesKeyedByUuid));

        // Succeeded Approve Timesheet Timesheet Entries
        yield put(succeededApproveTimesheetTimesheetEntries(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet entries approved successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                approveTimesheetEntriesModal: undefined
            })
        );
    } catch (error) {
        yield put(failedApproveTimesheetTimesheetEntries(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('TIMESHEET_ENTRIES_APPROVE', { _error: message }));
    }
}

export function* watchTimesheetTimesheetEntriesApproveRequested() {
    yield takeLatest(
        TIMESHEET_TIMESHEET_ENTRIES_APPROVE_REQUESTED,
        approveTimesheetTimesheetEntries
    );
}

export default function* timesheetTimesheetEntriesSagas() {
    yield all([
        fork(watchTimesheetTimesheetEntriesManageRequested),
        fork(watchTimesheetTimesheetEntriesApproveRequested)
    ]);
}
